// https://github.com/focus-trap/focus-trap

export interface FocusTrapOptions {
  clickOutsideDeactivates?: boolean;
  escapeDeactivates?: boolean;
  fallbackFocus?: HTMLElement | string;
  initialFocus?: HTMLElement | string;
}

export interface FocusTrap {
  active: boolean;
  activate(): FocusTrap;
  deactivate(): FocusTrap;
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const focusableSelectors = [
    'a[href]:not([tabindex^="-"])',
    'area[href]:not([tabindex^="-"])',
    'input:not([disabled]):not([tabindex^="-"])',
    'select:not([disabled]):not([tabindex^="-"])',
    'textarea:not([disabled]):not([tabindex^="-"])',
    'button:not([disabled]):not([tabindex^="-"])',
    '[tabindex]:not([tabindex^="-"])',
    '[contenteditable="true"]:not([tabindex^="-"])',
  ];

  const candidates = Array.from(container.querySelectorAll(focusableSelectors.join(","))) as HTMLElement[];

  return candidates.filter((el) => {
    return el.offsetWidth > 0 && el.offsetHeight > 0 && window.getComputedStyle(el).visibility !== "hidden";
  });
}

function getActualTarget(event: Event): HTMLElement {
  if (event.target instanceof HTMLElement) {
    if (event.target.shadowRoot && typeof event.composedPath === "function") {
      return event.composedPath()[0] as HTMLElement;
    }
    return event.target;
  }
  return document.body;
}

function getNode(target: HTMLElement | string | undefined, fallback: HTMLElement): HTMLElement {
  if (!target) {
    return fallback;
  }

  if (typeof target === "string") {
    const node = document.querySelector(target) as HTMLElement;
    return node || fallback;
  }

  return target;
}

export function createFocusTrap(element: HTMLElement | string, userOptions: FocusTrapOptions = {}): FocusTrap {
  const container = typeof element === "string" ? (document.querySelector(element) as HTMLElement) : element;

  if (!container) {
    throw new Error("Invalid focus trap container");
  }

  const options = {
    clickOutsideDeactivates: false,
    escapeDeactivates: true,
    fallbackFocus: container,
    initialFocus: undefined,
    ...userOptions,
  };

  let active = false;
  let previouslyFocusedElement: HTMLElement | null = null;

  function handleFocus(event: FocusEvent) {
    if (!active) return;

    const target = getActualTarget(event) as HTMLElement;

    if (container.contains(target)) {
      return;
    }

    event.stopPropagation();

    const focusableElements = getFocusableElements(container);

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    } else {
      const fallbackElement = getNode(options.fallbackFocus, container);
      fallbackElement.focus();
    }
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (!active) return;

    if (event.key === "Tab") {
      const focusableElements = getFocusableElements(container);

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    } else if (event.key === "Escape" && options.escapeDeactivates) {
      deactivate();
    }
  }

  function handleClick(event: MouseEvent) {
    if (!active) return;

    const target = getActualTarget(event) as HTMLElement;

    if (container.contains(target)) {
      return;
    }

    if (options.clickOutsideDeactivates) {
      deactivate();
    } else {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  function addListeners() {
    document.addEventListener("focus", handleFocus, true);
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("mousedown", handleClick, true);
    document.addEventListener("touchstart", handleClick, true);
  }

  function removeListeners() {
    document.removeEventListener("focus", handleFocus, true);
    document.removeEventListener("keydown", handleKeyDown, true);
    document.removeEventListener("mousedown", handleClick, true);
    document.removeEventListener("touchstart", handleClick, true);
  }

  function activate(): FocusTrap {
    if (active) return trap;

    previouslyFocusedElement = document.activeElement as HTMLElement;

    active = true;
    addListeners();

    const initialElement = options.initialFocus ? getNode(options.initialFocus, container) : container;

    setTimeout(() => {
      if (active) {
        try {
          initialElement.focus();
        } catch (e) {
          console.warn("Error focusing initial element", e);
        }
      }
    }, 0);

    return trap;
  }

  function deactivate(): FocusTrap {
    if (!active) return trap;

    removeListeners();

    active = false;

    if (previouslyFocusedElement && previouslyFocusedElement.focus) {
      try {
        previouslyFocusedElement.focus();
      } catch (e) {
        console.warn("Error restoring focus", e);
      }
    }

    return trap;
  }

  const trap: FocusTrap = {
    get active() {
      return active;
    },
    activate,
    deactivate,
  };

  return trap;
}
